import { memo, useCallback } from 'react'
import { useTranslations } from '@/core/translation'
import { Divider } from '@mui/material'
import { footerStyles as styles } from './Footer.style'
import { copyRightLinks, linksData, socialsData } from './Footer.data'
import { BookSeatsLogo, FanaticsLogo } from '@/assets'
import { useDomainInfo } from '@/utils'
import { Box, Container } from '@/components/Layout'
import { Text } from '@/components/Data'
import { Link } from '@/components/Navigation'
import { Button, Input } from '@/components/Inputs'
import { ANALYTICS_EVENTS, useAnalytics } from '@/core/analytics'
import { withErrorBoundary } from '@/components/Utils'
import ConfirmationModal from '../ConfirmationModal'
import { useFooterSocialController } from './Footer.social.controller'

function Footer() {
  return (
    <Container sx={styles.wrapper} component={'footer'}>
      <Socials />
      <Links />
      <Divider variant="middle" sx={styles.divider} />
      <CopyRightSection />
    </Container>
  )
}

const Socials = () => {
  const t = useTranslations('landingPage')
  const { isFanaticsDomain } = useDomainInfo()
  const { onSubscribeClick, form, isSubscribing, subscribeMessage, closeSubscribeModal } =
    useFooterSocialController()

  const {
    register,
    formState: { errors },
    watch,
  } = form

  return (
    <Box sx={styles.socialWrapper}>
      <Box sx={styles.socialBrandWrapper}>
        <Box sx={styles.brandLogo} component={isFanaticsDomain ? FanaticsLogo : BookSeatsLogo} />
        <Text sx={styles.socialSubHeading}>{t('momentsMadeTitle')}</Text>
        <Box sx={styles.socialImageWrapper}>
          {socialsData.map((image) => {
            const { image: Icon, link } = image
            return (
              <Box key={link} sx={styles.socialIcons}>
                <Link to={link}>
                  <Box component={Icon} sx={styles.socialIcon} />
                </Link>
              </Box>
            )
          })}
        </Box>
      </Box>
      <Box sx={styles.subscribeWrapper}>
        <Text sx={styles.socialSubHeading}>{t('subscribeDescription')}</Text>
        <Input
          value={watch('email')}
          {...register('email')}
          label={t('subscribeInputLabel')}
          placeholder={t('subscribeInputPlaceholder')}
          labelPlacement="outside"
          inputProps={{ sx: styles.input }}
          labelProps={{ sx: styles.inputLabel }}
          inputWrapperProps={{
            sx: styles.inputWrapperStyle,
          }}
          errorText={errors?.email?.message}
        />
        <Button
          sx={isFanaticsDomain ? styles.subscribeButtonFanatics : styles.subscribeButton}
          color="warmIvory"
          size="small"
          onClick={onSubscribeClick}
          loading={isSubscribing}>
          {t('subscribe')}
        </Button>
      </Box>

      <ConfirmationModal
        open={Boolean(subscribeMessage)}
        onConfirmClick={closeSubscribeModal}
        title={subscribeMessage}
        confirmButtonText={t('continue')}
        hideCancel
      />
    </Box>
  )
}

const Links = () => {
  const analytics = useAnalytics()

  const onLinkClick = useCallback(
    (navText: string, navButton: string) => () =>
      analytics.sendNavigationEvent(
        navText,
        `${navText}.${navButton}`,
        ANALYTICS_EVENTS.NAVIGATION_LOCATION.FOOTER,
      ),
    [analytics],
  )

  return (
    <Box sx={styles.linkSection}>
      {linksData.map((category) => (
        <Box key={category.title} sx={styles.linkCategory}>
          <Text sx={styles.linkCategoryTitle}>{category.title}</Text>
          <Box sx={styles.linkListWrapper}>
            {category.links.map((link) => (
              <Link key={link.name} to={link.link} onClick={onLinkClick(category.title, link.name)}>
                <Text sx={styles.linkText}>{link.name}</Text>
              </Link>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

const CopyRightSection = () => {
  const t = useTranslations('landingPage')

  return (
    <Box sx={styles.copyRightSection}>
      <Text sx={styles.copyRightText}>{t('copyRight')}</Text>
      <Box sx={styles.copyRightLinks}>
        {copyRightLinks.map((link) => (
          <Link key={link.name} to={link.link}>
            <Text sx={styles.copyRightLink}>{link.name}</Text>
          </Link>
        ))}
      </Box>
    </Box>
  )
}

export default memo(withErrorBoundary(Footer))
